import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Mail, Send, Key, CheckCircle, XCircle, Loader2, ArrowLeft, Clock } from 'lucide-react';
import { settingsAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';

const AdminEmailSettings = () => {
  const { language } = useLanguage();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const [settings, setSettings] = useState({
    resend_api_key: '',
    sender_email: 'onboarding@resend.dev',
    sender_name: 'ColaboraChile',
    notifications_enabled: false,
    has_api_key: false,
    resend_api_key_masked: ''
  });

  const t = {
    es: {
      title: 'Configuracion de Email',
      subtitle: 'Configura las notificaciones por correo electronico para los postulantes',
      apiKeyLabel: 'API Key de Resend',
      apiKeyPlaceholder: 're_xxxxxxxxxx...',
      apiKeyHelp: 'Obten tu API key gratis en resend.com',
      senderEmail: 'Email remitente',
      senderName: 'Nombre remitente',
      enableNotifications: 'Activar notificaciones',
      enableDesc: 'Cuando esta activo, se enviaran emails automaticos al cambiar el estado de las postulaciones',
      save: 'Guardar Configuracion',
      saving: 'Guardando...',
      testEmail: 'Enviar Email de Prueba',
      testEmailPlaceholder: 'correo@ejemplo.com',
      sendTest: 'Enviar Prueba',
      sendingTest: 'Enviando...',
      saveSuccess: 'Configuracion guardada',
      saveSuccessDesc: 'Los cambios fueron aplicados correctamente',
      testSuccess: 'Email enviado',
      testSuccessDesc: 'Revisa tu bandeja de entrada',
      emailLogs: 'Historial de Emails',
      noLogs: 'No hay emails enviados aun',
      back: 'Volver al Panel',
      status: 'Estado',
      recipient: 'Destinatario',
      subject: 'Asunto',
      date: 'Fecha',
      sent: 'Enviado',
      failed: 'Fallido',
      configured: 'Configurado',
      notConfigured: 'Sin configurar',
      currentKey: 'Key actual'
    },
    en: {
      title: 'Email Configuration',
      subtitle: 'Configure email notifications for applicants',
      apiKeyLabel: 'Resend API Key',
      apiKeyPlaceholder: 're_xxxxxxxxxx...',
      apiKeyHelp: 'Get your free API key at resend.com',
      senderEmail: 'Sender email',
      senderName: 'Sender name',
      enableNotifications: 'Enable notifications',
      enableDesc: 'When active, automatic emails will be sent when application status changes',
      save: 'Save Configuration',
      saving: 'Saving...',
      testEmail: 'Send Test Email',
      testEmailPlaceholder: 'email@example.com',
      sendTest: 'Send Test',
      sendingTest: 'Sending...',
      saveSuccess: 'Configuration saved',
      saveSuccessDesc: 'Changes were applied successfully',
      testSuccess: 'Email sent',
      testSuccessDesc: 'Check your inbox',
      emailLogs: 'Email History',
      noLogs: 'No emails sent yet',
      back: 'Back to Panel',
      status: 'Status',
      recipient: 'Recipient',
      subject: 'Subject',
      date: 'Date',
      sent: 'Sent',
      failed: 'Failed',
      configured: 'Configured',
      notConfigured: 'Not configured',
      currentKey: 'Current key'
    }
  }[language];

  useEffect(() => {
    loadSettings();
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await settingsAPI.getEmailSettings();
      setSettings(prev => ({
        ...prev,
        ...data,
        resend_api_key: '' // Don't prefill raw key
      }));
    } catch (err) {
      console.error('Error loading settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    try {
      setLogsLoading(true);
      const data = await settingsAPI.getEmailLogs();
      setLogs(data);
    } catch (err) {
      console.error('Error loading logs:', err);
    } finally {
      setLogsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await settingsAPI.updateEmailSettings({
        resend_api_key: settings.resend_api_key || null,
        sender_email: settings.sender_email,
        sender_name: settings.sender_name,
        notifications_enabled: settings.notifications_enabled
      });
      toast({ title: t.saveSuccess, description: t.saveSuccessDesc });
      loadSettings();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Error saving settings', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    if (!testEmail) return;
    try {
      setSendingTest(true);
      await settingsAPI.sendTestEmail(testEmail);
      toast({ title: t.testSuccess, description: t.testSuccessDesc });
      loadLogs();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Error sending test email', variant: 'destructive' });
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="email-settings-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button asChild variant="ghost" size="sm">
          <Link to="/dashboard"><ArrowLeft className="w-4 h-4 mr-1" /> {t.back}</Link>
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#6949b6' }}>
          <Mail className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t.title}</h1>
          <p className="text-gray-600 text-sm">{t.subtitle}</p>
        </div>
      </div>

      {/* API Key & Config */}
      <Card className="border-2 border-gray-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-5 h-5" />
            {t.apiKeyLabel}
          </CardTitle>
          <CardDescription>
            <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">
              {t.apiKeyHelp}
            </a>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {settings.has_api_key && (
            <div className="flex items-center gap-2 text-sm">
              <Badge className="bg-green-100 text-green-700 border-green-300">
                <CheckCircle className="w-3 h-3 mr-1" /> {t.configured}
              </Badge>
              <span className="text-gray-500">{t.currentKey}: <code className="bg-gray-100 px-2 py-0.5 rounded text-xs">{settings.resend_api_key_masked}</code></span>
            </div>
          )}
          {!settings.has_api_key && (
            <Badge className="bg-red-100 text-red-700 border-red-300">
              <XCircle className="w-3 h-3 mr-1" /> {t.notConfigured}
            </Badge>
          )}

          <div className="space-y-2">
            <Label>{settings.has_api_key ? (language === 'es' ? 'Nueva API Key (dejar vacio para mantener actual)' : 'New API Key (leave empty to keep current)') : t.apiKeyLabel}</Label>
            <Input
              type="password"
              value={settings.resend_api_key}
              onChange={(e) => setSettings({ ...settings, resend_api_key: e.target.value })}
              placeholder={t.apiKeyPlaceholder}
              data-testid="api-key-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t.senderName}</Label>
              <Input
                value={settings.sender_name}
                onChange={(e) => setSettings({ ...settings, sender_name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>{t.senderEmail}</Label>
              <Input
                value={settings.sender_email}
                onChange={(e) => setSettings({ ...settings, sender_email: e.target.value })}
              />
            </div>
          </div>

          <div className="flex items-center justify-between p-4 rounded-lg border-2 border-gray-200 bg-gray-50">
            <div>
              <p className="font-medium text-gray-900">{t.enableNotifications}</p>
              <p className="text-sm text-gray-500">{t.enableDesc}</p>
            </div>
            <Switch
              checked={settings.notifications_enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, notifications_enabled: checked })}
              data-testid="notifications-toggle"
            />
          </div>

          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full text-white"
            style={{ backgroundColor: '#6949b6' }}
            data-testid="save-email-settings"
          >
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t.saving}</> : t.save}
          </Button>
        </CardContent>
      </Card>

      {/* Test Email */}
      <Card className="border-2 border-gray-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="w-5 h-5" />
            {t.testEmail}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <Input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder={t.testEmailPlaceholder}
              className="flex-1"
              data-testid="test-email-input"
            />
            <Button
              onClick={handleSendTest}
              disabled={sendingTest || !testEmail || !settings.has_api_key}
              variant="outline"
              className="border-2"
              style={{ borderColor: '#6949b6', color: '#6949b6' }}
              data-testid="send-test-email-btn"
            >
              {sendingTest ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t.sendingTest}</> : <><Send className="w-4 h-4 mr-2" /> {t.sendTest}</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Email Logs */}
      <Card className="border-2 border-gray-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            {t.emailLogs}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {logsLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
          ) : logs.length === 0 ? (
            <p className="text-gray-400 text-center py-8 text-sm">{t.noLogs}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-gray-500">
                    <th className="py-2 pr-4">{t.status}</th>
                    <th className="py-2 pr-4">{t.recipient}</th>
                    <th className="py-2 pr-4">{t.subject}</th>
                    <th className="py-2">{t.date}</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, idx) => (
                    <tr key={idx} className="border-b border-gray-100">
                      <td className="py-2 pr-4">
                        {log.status === 'sent' ? (
                          <Badge className="bg-green-100 text-green-700 text-xs"><CheckCircle className="w-3 h-3 mr-1" />{t.sent}</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700 text-xs"><XCircle className="w-3 h-3 mr-1" />{t.failed}</Badge>
                        )}
                      </td>
                      <td className="py-2 pr-4 text-gray-700">{log.recipient}</td>
                      <td className="py-2 pr-4 text-gray-700 max-w-[250px] truncate">{log.subject}</td>
                      <td className="py-2 text-gray-500 text-xs">
                        {new Date(log.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminEmailSettings;
